import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChapterOpening } from './ChapterOpening';
import { IntroScreen } from './IntroScreen';
import { ProfileScreen } from './ProfileScreen';
import { ProgressHeader } from './ProgressHeader';
import { QuestionScreen } from './QuestionScreen';
import { RewardScreen } from './RewardScreen';
import { SummaryScreen } from './SummaryScreen';
import { chapters } from '../data/chapters';
import { defaultPeerGroup, roleOptions } from '../data/profile';
import { usePulseFlow } from '../hooks/usePulseFlow';
const variants = {
  enter: (d: number) => ({
    opacity: 0,
    x: d * 40,
    scale: 0.98
  }),
  center: {
    opacity: 1,
    x: 0,
    scale: 1
  },
  exit: (d: number) => ({
    opacity: 0,
    x: d * -40,
    scale: 0.98
  })
};
interface PulseExperienceProps {
  onOpenResults?: () => void;
  /** Auto-plays between the first two screens (used for campaign previews). */
  previewLoop?: boolean;
}
export function PulseExperience({
  onOpenResults = () => undefined,
  previewLoop = false
}: PulseExperienceProps) {
  const flow = usePulseFlow();
  const {
    step,
    stepIndex,
    direction,
    answers,
    next,
    back
  } = flow;
  useEffect(() => {
    if (!previewLoop) return;
    const t = window.setTimeout(() => stepIndex === 0 ? next() : back(), 3400);
    return () => window.clearTimeout(t);
  }, [previewLoop, stepIndex, next, back]);
  let screen: React.ReactNode = null;
  let chapterIndex: number | null = null;
  const peerGroup = roleOptions.find(r => r.id === flow.role)?.peerGroup ?? defaultPeerGroup;
  if (step.kind === 'intro') {
    screen = <IntroScreen onBegin={flow.next} data-id="element-481" data-mp="8378oz0fo142" />;
  } else if (step.kind === 'profile') {
    screen = <ProfileScreen initialRole={flow.role} onContinue={flow.chooseRole} data-id="element-482" data-mp="34vyuoa51qlu" />;
  } else if (step.kind === 'summary') {
    screen = <SummaryScreen answers={answers} peerGroup={peerGroup} onRestart={flow.restart} onOpenResults={onOpenResults} data-id="element-483" data-mp="2sur0o4662aqa" />;
  } else {
    chapterIndex = step.chapterIndex;
    const chapter = chapters[step.chapterIndex];
    const answer = answers[chapter.id];
    if (step.kind === 'opening') {
      screen = <ChapterOpening chapter={chapter} onBegin={flow.next} data-id="element-484" data-mp="1jwheckcdtx8g" />;
    } else if (step.kind === 'question') {
      screen = <QuestionScreen eyebrow="Your view" question={chapter.question} options={chapter.options} accent={chapter.accent} initialSelected={answer?.main ?? null} onAnswer={id => flow.answerMain(chapter.id, id)} data-id="element-485" data-mp="15zcc6vfshiih" />;
    } else if (step.kind === 'followup') {
      const main = chapter.options.find(o => o.id === answer?.main);
      const followUp = answer?.main ? chapter.followUps[answer.main] : undefined;
      screen = followUp ? <QuestionScreen eyebrow="One step deeper" question={followUp.question} options={followUp.options} accent={chapter.accent} initialSelected={answer?.followUp ?? null} contextChip={main ? `You chose: ${main.short}` : undefined} onAnswer={id => flow.answerFollowUp(chapter.id, id)} onSkip={() => flow.answerFollowUp(chapter.id, null)} data-id="element-486" data-mp="d8kjw91uhe0f" /> : <RewardScreen chapter={chapter} answer={answer} peerGroup={peerGroup} isLast={false} onContinue={flow.next} data-id="element-487" data-mp="8l2wkr2lcpfk" />;
    } else {
      screen = <RewardScreen chapter={chapter} answer={answer} peerGroup={peerGroup} isLast={step.chapterIndex === chapters.length - 1} onContinue={flow.next} data-id="element-488" data-mp="hkq0bjism7bs" />;
    }
  }
  return <div className="relative h-full w-full overflow-hidden bg-white text-navy-900" data-id="element-489" data-mp="r9hqbm4amhk2">
      {chapterIndex !== null && <ProgressHeader chapterIndex={chapterIndex} chapterProgress={flow.chapterProgress} canGoBack={flow.canGoBack} onBack={flow.back} data-id="element-490" data-mp="mbzpkj0fj8n5" />}
      <AnimatePresence mode="wait" custom={direction} initial={false} data-id="element-491" data-mp="wb71r2vfgx8o">
        <motion.div key={stepIndex} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
        duration: 0.45,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute inset-0" data-id="element-492" data-mp="2pt4lxg5qpr2w">
          {screen}
        </motion.div>
      </AnimatePresence>
    </div>;
}